/** @format */

import { Editor } from "@tiptap/react";
import { MessageCircle, X } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "../../hooks/useToast";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import { useTrackChangesState } from "../../store/useTrackChangesStore";
import { Toast } from "../../components/ui/Toast";

interface Props {
  editor: Editor | null;
  sopId: string;
  sopTitle: string;
  versionId: string | undefined;
  stepIndex: number;
  /**
   * When set, the bubble submits feedback against this AI candidate rather than the
   * published version.
   */
  candidateId?: string;
  /**
   * Controls whether the feedback bubble is active. When false the bubble never
   * appears, even if candidateId is set. Defaults to true.
   * Use this to gate candidate feedback behind a "Deny" interaction.
   */
  feedbackEnabled?: boolean;
  /**
   * Extra unsaved state (e.g. title page) combined with track-changes
   * for the "save before feedback" guard.
   */
  unsavedChanges?: boolean;
}

const BUBBLE_GAP = 10;

export const FeedbackBubbleMenu: FC<Props> = ({
  editor,
  sopId,
  sopTitle,
  versionId,
  stepIndex,
  candidateId,
  feedbackEnabled = true,
  unsavedChanges = false,
}) => {
  const [bubbleRect, setBubbleRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState("");
  const { submitting, submitFeedback } = useFeedbackStore();
  const { toast, showToast, hideToast } = useToast();
  const { hasUnsavedChanges: trackHasUnsaved } = useTrackChangesState();
  const hasUnsavedChanges = trackHasUnsaved || unsavedChanges;

  const rafRef = useRef<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  /**
   * When true, position tracking is frozen. We set this the moment the user
   * opens the form so that selection changes (editor blur, textarea focus, etc.)
   * can't collapse the bubble while the user is typing.
   */
  const lockedRef = useRef(false);

  // ─── Close helper ─────────────────────────────────────────────────────────

  const close = useCallback(() => {
    lockedRef.current = false;
    setExpanded(false);
    setBubbleRect(null);
    setComment("");
  }, []);

  // ─── Position tracking ────────────────────────────────────────────────────

  // NOTE: `expanded` is intentionally NOT in the dep array — we use lockedRef
  // instead so we don't re-register editor listeners on every expand/collapse.
  const computePosition = useCallback(() => {
    if (lockedRef.current) return;
    if (!editor) return;

    const { from, to } = editor.state.selection;
    if (from === to) {
      setBubbleRect(null);
      return;
    }

    const domSel = window.getSelection();
    if (!domSel || domSel.rangeCount === 0) {
      setBubbleRect(null);
      return;
    }

    const range = domSel.getRangeAt(0);

    const editorDom = editor?.view?.dom;
    let node: Node | null = range.commonAncestorContainer;
    // Walk up from the common ancestor to see if we're inside the editor view.
    while (node && node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }
    let insideEditor = false;
    let walker: Node | null = node;
    while (walker && editorDom && walker !== document.body) {
      if (walker === editorDom) {
        insideEditor = true;
        break;
      }
      walker = walker.parentNode;
    }

    if (!insideEditor) {
      setBubbleRect(null);
      return;
    }

    const rect = range.getBoundingClientRect();
    if (!rect.width) {
      setBubbleRect(null);
      return;
    }

    // Mini pill width ~90px; keep it viewport-safe
    const left = Math.max(
      8,
      Math.min(rect.left + rect.width / 2 - 45, window.innerWidth - 98),
    );

    setBubbleRect({ top: rect.top - BUBBLE_GAP, left });
  }, [editor]); // ← no `expanded` dep

  useEffect(() => {
    if (!editor) return;
    const schedule = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        computePosition();
      });
    };
    editor.on("selectionUpdate", schedule);
    editor.on("transaction", schedule);
    return () => {
      editor.off("selectionUpdate", schedule);
      editor.off("transaction", schedule);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [editor, computePosition]);

  // ─── Outside-click collapse ───────────────────────────────────────────────

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        close();
      }
    };
    // Defer one tick so the triggering click doesn't immediately close
    const id = window.setTimeout(
      () => document.addEventListener("mousedown", handler),
      0,
    );
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", handler);
    };
  }, [expanded, close]);

  // ─── Escape key ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [expanded, close]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!comment.trim() || !versionId) return;
    // For candidate feedback the "unsaved SOP edits" warning does not apply.
    if (hasUnsavedChanges && !candidateId) {
      showToast(
        "Save changes first",
        "You have unsaved SOP edits. Save your changes before sending feedback.",
        "warning",
      );
      return;
    }
    try {
      // Close immediately so this UI can't sit above global loaders/spinners.
      // If submission fails, the toast communicates the error and the user can retry.
      close();

      const selectionPayload: {
        selectionFrom?: number;
        selectionTo?: number;
        selectedText?: string;
      } = {};
      if (editor) {
        const { from, to } = editor.state.selection;
        if (from !== to) {
          selectionPayload.selectionFrom = from;
          selectionPayload.selectionTo = to;
          selectionPayload.selectedText = editor.state.doc.textBetween(
            from,
            to,
            " ",
          );
        }
      }
      await submitFeedback(sopId, {
        sopTitle,
        versionId,
        ...(candidateId ? { candidateId } : {}),
        stepIndex,
        comment: comment.trim(),
        ...selectionPayload,
      });
      showToast("Feedback submitted", "", "success");
    } catch {
      showToast("Failed to submit feedback", "Please try again.", "error");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  // Candidate feedback is gated behind the deny flow.
  if (candidateId && !feedbackEnabled) return null;

  const toastPortal =
    toast.open || (bubbleRect && versionId)
      ? createPortal(
          <Toast
            open={toast.open}
            onOpenChange={(open) => !open && hideToast()}
            title={toast.title ?? ""}
            description={toast.description}
            type={toast.type}
            duration={toast.duration}
          />,
          document.body,
        )
      : null;

  const bubble =
    bubbleRect && versionId ? (
    <div
      ref={formRef}
      style={{
        position: "fixed",
        top: bubbleRect.top,
        left: bubbleRect.left,
        transform: "translateY(-100%)",
        zIndex: 9999,
      }}
      // Stop propagation so ProseMirror doesn't see these events
      onMouseDown={(e) => e.stopPropagation()}
    >
      {!expanded ? (
          // ── Mini pill ────────────────────────────────────────────────────
          <button
            // preventDefault keeps editor focus + selection intact when clicking
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              lockedRef.current = true;
              setExpanded(true);
            }}
            className={[
              "flex items-center gap-1.5 rounded border px-2 py-1",
              "text-xs font-semibold tracking-wide",
              "border-slate-600/80 bg-slate-800/40 text-slate-200",
              "shadow-lg shadow-black/40 backdrop-blur",
              "hover:border-fuchsia-500/60 hover:text-fuchsia-200 hover:bg-slate-700/80",
              "transition-all duration-150",
            ].join(" ")}
            title="Add feedback for this text"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span>Add feedback</span>
          </button>
        ) : (
          // ── Expanded form ────────────────────────────────────────────────
          <div className="w-80 rounded-xl border border-slate-600/80 bg-slate-900/90 shadow-2xl shadow-black/60 backdrop-blur">
            <div className="flex items-center justify-between border-b border-slate-700/70 px-3.5 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500/25 to-pink-500/25 border border-purple-500/20">
                  <MessageCircle className="h-4 w-4 text-fuchsia-200" />
                </span>
                Feedback
              </div>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={close}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200 transition-colors"
                aria-label="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="p-3.5 space-y-3">
              {hasUnsavedChanges && !candidateId && (
                <div className="rounded border text-center border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                  You have unsaved SOP edits. Save your changes before sending
                  feedback.
                </div>
              )}
              <textarea
                autoFocus
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What should be improved about this text?"
                rows={4}
                disabled={hasUnsavedChanges && !candidateId}
                className={[
                  "w-full resize-none rounded border px-3 py-2.5 text-sm",
                  "border-slate-700 bg-slate-950/40 text-slate-100 placeholder-slate-500",
                  "focus:border-fuchsia-500/60 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/15",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                ].join(" ")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />

              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSubmit}
                disabled={submitting || !comment.trim() || (hasUnsavedChanges && !candidateId)}
                className={[
                  "w-full rounded px-3 py-2 text-sm font-semibold text-white",
                  "bg-gradient-to-r from-purple-600 to-pink-600",
                  "hover:from-purple-500 hover:to-pink-500",
                  "shadow-lg shadow-purple-500/15",
                  "disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none",
                  "transition-all",
                ].join(" ")}
              >
                {submitting ? "Sending…" : "Send feedback"}
              </button>

              <p className="text-[11px] text-slate-500">
                Press{" "}
                <span className="font-semibold text-slate-400">⌘/Ctrl</span> +{" "}
                <span className="font-semibold text-slate-400">Enter</span> to
                send.
              </p>
            </div>
          </div>
        )}
    </div>
    ) : null;

  return (
    <>
      {toastPortal}
      {bubble ? createPortal(bubble, document.body) : null}
    </>
  );
};
